<script setup lang="ts">
import { useFieldValue } from 'vee-validate';
import { computed, toRef } from 'vue';
import {
  ElectronBeamQualityDoseRateValueListDto,
  PhotonBeamQualityDoseRateValueListDto,
} from '@/api/generator';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import { I18nKey } from '@/utils/typings.ts';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import VeeDiscreteNumericList from '@/components/forms/dataTypes/VeeDiscreteNumericList.vue';
import VeeNumericValueField from '@/components/forms/dataTypes/VeeNumericValueField.vue';
import { useFormMode } from '@/components/forms/useFormMode';

import { useTranslation } from 'i18next-vue';
import {
  alphabeticalStringCompare,
  cascadedCompareFunction,
  optionalNumberCompare,
} from '@/utils/compareAndSort';
const { t } = useTranslation();

type BeamQualityType = 'NominalPhotonBeamEnergy' | 'NominalElectronBeamEnergy';

const props = defineProps<{
  name: string;
  beamQualityType: BeamQualityType;
}>();

const emits = defineEmits<{
  select: [];
}>();

const mode = useFormMode();

type BeamQualityDoseRateValueList =
  | PhotonBeamQualityDoseRateValueListDto[]
  | ElectronBeamQualityDoseRateValueListDto[];

const beamQualities = useFieldValue<BeamQualityDoseRateValueList>(toRef(() => props.name));

const hasBeamQualities = computed(() => !!beamQualities.value.length);

const enabledBeamQualities = computed(() =>
  beamQualities.value
    .map((beamQuality, index) => ({ ...beamQuality, index }))
    .filter((beamQuality) => beamQuality.isEnabled === true),
);

const hasEnabledBeamQualities = computed(() => !!enabledBeamQualities.value.length);

const displayedBeamQualitites = computed(() => {
  if (mode.value === 'details') {
    return enabledBeamQualities.value;
  } else {
    return beamQualities.value.map((beamQuality, index) => ({ ...beamQuality, index }));
  }
});

const strings = computed(() => {
  const beamQualitySpecificStrings = {
    NominalPhotonBeamEnergy: {
      tableHeader: t('PhotonBeamQualityDoseRates'),
      energyHeader: t('QuantityNameLabelEnum.NominalPhotonBeamEnergy'),
      noBeamQualitiesMessage: t('NoPhotonBeamQualitiesAndDoseRatesEnabled'),
    },
    NominalElectronBeamEnergy: {
      tableHeader: t('ElectronBeamQualityDoseRates'),
      energyHeader: t('QuantityNameLabelEnum.NominalElectronBeamEnergy'),
      noBeamQualitiesMessage: t('NoElectronBeamQualitiesAndDoseRatesEnabled'),
    },
  };

  return beamQualitySpecificStrings[props.beamQualityType];
});

const headers = [
  {
    title: t('Enabled'),
    key: 'isEnabled',
    sortable: false,
    width: '0%',
  },
  {
    title: t('Name'),
    key: 'name',
    sortable: true,
  },
  {
    title: t('RadiationType'),
    key: 'radiationType.value.name',
    sortable: false,
  },
  {
    title: strings.value.energyHeader,
    key: 'energy',
    sortRaw: cascadedCompareFunction<
      PhotonBeamQualityDoseRateValueListDto | ElectronBeamQualityDoseRateValueListDto
    >([
      (a, b) => optionalNumberCompare(a.energy.value, b.energy.value),
      (a, b) => alphabeticalStringCompare(a.name, b.name),
    ]),
  },
  {
    title: t('PrimaryFluenceShaping'),
    key: 'primaryFluenceShaping.value.name',
    sortable: false,
  },
  {
    title: t('HighDoseTechniqueType'),
    key: 'highDoseTechniqueType.value.name',
    sortable: false,
  },
  {
    title: t('DoseRatesForStaticBeams'),
    key: 'selectedDoseRates',
    sortable: false,
    width: '25%',
  },
].filter(
  (header) =>
    !(
      props.beamQualityType === 'NominalElectronBeamEnergy' &&
      ['primaryFluenceShaping.value.name'].includes(header.key)
    ),
);
</script>

<template>
  <div v-if="hasBeamQualities">
    <h6 class="text-primary">
      {{ strings.tableHeader }}
    </h6>

    <OverviewTable
      v-if="hasEnabledBeamQualities || mode !== 'details'"
      :items="displayedBeamQualitites"
      :data-testid="`DataTable_BeamQualities_${beamQualityType}`"
      :headers="headers"
      :zebra="true"
      :noSpacing="true"
      itemValue="index"
      :defaultSortBy="[{ key: 'energy' }]"
      :showItemsCount="false"
    >
      <template #header.radiationType.value.name="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ headerProps.column.title }}
        </OverviewTableHeader>
      </template>

      <template #header.energy="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ headerProps.column.title }}
        </OverviewTableHeader>
      </template>

      <template #header.primaryFluenceShaping.value.name="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ headerProps.column.title }}
        </OverviewTableHeader>
      </template>

      <template #header.highDoseTechniqueType.value.name="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ headerProps.column.title }}
        </OverviewTableHeader>
      </template>

      <template #header.selectedDoseRates="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ headerProps.column.title }}
        </OverviewTableHeader>
      </template>

      <template #item.isEnabled="{ item }">
        <VeeCheckbox
          :name="`${name}[${item.index}].isEnabled`"
          :mode="'edit'"
          :disabled="mode === 'details'"
          @change="emits('select')"
        />
      </template>

      <template #item.radiationType.value.name="{ value: radiationType }">
        {{ radiationType ? $t(radiationType as I18nKey) : EMPTY_FIELD_SIGN }}
      </template>

      <template #item.energy="{ item }">
        <VeeNumericValueField :name="`${name}[${item.index}].energy`" mode="details" />
      </template>

      <template #item.primaryFluenceShaping.value.name="{ value: primaryFluenceShaping }">
        {{ primaryFluenceShaping ? $t(primaryFluenceShaping as I18nKey) : EMPTY_FIELD_SIGN }}
      </template>

      <template #item.highDoseTechniqueType.value.name="{ value: highDoseTechniqueType }">
        {{ highDoseTechniqueType ? $t(highDoseTechniqueType as I18nKey) : EMPTY_FIELD_SIGN }}
      </template>

      <template #item.selectedDoseRates="{ item: beamQuality }">
        <VeeDiscreteNumericList
          :disabled="!beamQuality.isEnabled"
          :name="`${name}[${beamQuality.index}].selectedDoseRates`"
          :mode="mode"
        />
      </template>
    </OverviewTable>

    <p
      v-if="!hasEnabledBeamQualities && mode === 'details'"
      class="text-body-1 font-weight-semibold text-primary-lighten-2"
    >
      {{ strings.noBeamQualitiesMessage }}
    </p>
  </div>
</template>

<style lang="css" scoped>
.overviewTable:deep(tr:has(.v-checkbox.selected:not(.v-checkbox.v-input--disabled))) {
  td {
    background-color: rgb(var(--v-theme-secondary-lighten-4)) !important;

    &:not(:first-child) {
      border-left: 1px solid rgb(var(--v-theme-primary-lighten-3)) !important;
    }
  }
}
</style>
